import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  InputNumber,
  Radio,
  Checkbox,
  DatePicker,
  Select,
  Upload,
  Button,
  Segmented,
  Tag,
  Typography,
  Space,
  Flex,
  theme,
  Slider,
  Table,
} from "antd";
import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { FIELD_TYPES } from "../../../../../../domain/clinicalForms";

const optionsToAntd = (opts) =>
  (opts || []).map((o) =>
    typeof o === "string" ? { label: o, value: o } : { label: o.label, value: o.value }
  );

const renderControl = (field, value, onChange) => {
  switch (field.type) {
    case FIELD_TYPES.TEXT:
      return (
        <Input
          variant="borderless"
          placeholder={field.placeholder || "Your answer"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case FIELD_TYPES.TEXTAREA:
      return (
        <Input.TextArea
          autoSize={{ minRows: 3 }}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case FIELD_TYPES.NUMBER:
      return (
        <InputNumber
          placeholder={field.placeholder || "0"}
          min={field.min}
          max={field.max}
          style={{ width: "100%" }}
          value={value ?? null}
          onChange={onChange}
        />
      );
    case FIELD_TYPES.DATE:
      return (
        <DatePicker
          style={{ width: "100%" }}
          placeholder={field.placeholder || "Select date"}
          value={value ?? null}
          onChange={onChange}
        />
      );
    case FIELD_TYPES.DATETIME:
      return (
        <DatePicker
          showTime
          style={{ width: "100%" }}
          placeholder={field.placeholder || "Select date & time"}
          value={value ?? null}
          onChange={onChange}
        />
      );
    case FIELD_TYPES.SELECT:
      return (
        <Select
          placeholder={field.placeholder || "Select"}
          options={optionsToAntd(field.options)}
          style={{ width: "100%" }}
          value={value ?? undefined}
          onChange={onChange}
          allowClear
        />
      );
    case FIELD_TYPES.MULTISELECT:
      return (
        <Select
          mode="multiple"
          placeholder={field.placeholder || "Select"}
          options={optionsToAntd(field.options)}
          style={{ width: "100%" }}
          value={value ?? []}
          onChange={onChange}
          allowClear
        />
      );
    case FIELD_TYPES.RADIO:
      return (
        <Radio.Group
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {(field.options || []).map((opt) => (
              <React.Fragment key={opt.value}>
                <Radio value={opt.value}>{opt.label}</Radio>
                {value === opt.value && opt.sub_questions?.length > 0 && (
                  <NestedFields fields={opt.sub_questions} />
                )}
              </React.Fragment>
            ))}
          </Space>
        </Radio.Group>
      );
    case FIELD_TYPES.CHECKBOX: {
      const selected = value ?? [];
      const toggle = (optValue, checked) => {
        const set = new Set(selected);
        if (checked) set.add(optValue);
        else set.delete(optValue);
        onChange(Array.from(set));
      };
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {(field.options || []).map((opt) => (
            <React.Fragment key={opt.value}>
              <Checkbox
                checked={selected.includes(opt.value)}
                onChange={(e) => toggle(opt.value, e.target.checked)}
              >
                {opt.label}
              </Checkbox>
              {selected.includes(opt.value) && opt.sub_questions?.length > 0 && (
                <NestedFields fields={opt.sub_questions} />
              )}
            </React.Fragment>
          ))}
        </Space>
      );
    }
    case FIELD_TYPES.SLIDER: {
      const min = field.min ?? 0;
      const max = field.max ?? 10;
      const step = field.step ?? 1;
      const current = typeof value === "number" ? value : min;
      return (
        <Flex align="center" gap={16} style={{ width: "100%" }}>
          <Slider
            min={min}
            max={max}
            step={step}
            value={current}
            onChange={onChange}
            style={{ flex: 1 }}
          />
          <Typography.Text strong style={{ minWidth: 32, textAlign: "right" }}>
            {current}
          </Typography.Text>
        </Flex>
      );
    }
    case FIELD_TYPES.TABLE: {
      const cols = field.columns || [];
      const minRows = field.min_rows ?? 0;
      const maxRows = field.max_rows ?? 50;
      const allowAddRows = field.allow_add_rows !== false;
      const rowsValue = Array.isArray(value) ? value : [];
      const currentRows =
        rowsValue.length < minRows
          ? [...rowsValue, ...Array(minRows - rowsValue.length).fill({})]
          : rowsValue;

      const setRows = (next) => onChange(next);
      const updateCell = (rowIdx, colId, v) => {
        const nextRows = currentRows.slice();
        nextRows[rowIdx] = { ...(nextRows[rowIdx] || {}), [colId]: v };
        setRows(nextRows);
      };
      const addRow = () => setRows([...currentRows, {}]);
      const deleteRow = (rowIdx) =>
        setRows(currentRows.filter((_, i) => i !== rowIdx));

      const canAdd = allowAddRows && currentRows.length < maxRows;
      const canDelete = (idx) => currentRows.length > minRows && idx >= 0;

      const cellOpts = (col) =>
        (col.options || []).map((o) => ({ label: o.label, value: o.value }));

      const renderCell = (col, val, onCellChange) => {
        switch (col.type) {
          case "textarea":
            return (
              <Input.TextArea
                size="small"
                autoSize={{ minRows: 1, maxRows: 4 }}
                value={val ?? ""}
                onChange={(e) => onCellChange(e.target.value)}
              />
            );
          case "number":
            return (
              <InputNumber
                size="small"
                value={val ?? null}
                onChange={onCellChange}
                style={{ width: "100%" }}
              />
            );
          case "date":
            return (
              <DatePicker
                size="small"
                value={val ? dayjs(val) : null}
                onChange={(d) => onCellChange(d ? d.toISOString() : null)}
                style={{ width: "100%" }}
              />
            );
          case "datetime":
            return (
              <DatePicker
                size="small"
                showTime
                value={val ? dayjs(val) : null}
                onChange={(d) => onCellChange(d ? d.toISOString() : null)}
                style={{ width: "100%" }}
              />
            );
          case "select":
            return (
              <Select
                size="small"
                value={val ?? undefined}
                options={cellOpts(col)}
                onChange={onCellChange}
                allowClear
                style={{ width: "100%" }}
              />
            );
          case "multiselect":
            return (
              <Select
                size="small"
                mode="multiple"
                value={Array.isArray(val) ? val : []}
                options={cellOpts(col)}
                onChange={onCellChange}
                allowClear
                style={{ width: "100%" }}
              />
            );
          case "checkbox":
            return (
              <Checkbox.Group
                value={Array.isArray(val) ? val : []}
                options={cellOpts(col)}
                onChange={onCellChange}
              />
            );
          case "radio":
            return (
              <Radio.Group
                value={val}
                onChange={(e) => onCellChange(e.target.value)}
              >
                {(col.options || []).map((opt) => (
                  <Radio key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio>
                ))}
              </Radio.Group>
            );
          case "file":
            return (
              <Upload>
                <Button size="small" icon={<UploadOutlined />}>
                  Upload
                </Button>
              </Upload>
            );
          default:
            return (
              <Input
                size="small"
                value={val ?? ""}
                onChange={(e) => onCellChange(e.target.value)}
              />
            );
        }
      };

      const columnWidth = (col) => {
        if (col.type === "textarea" || col.type === "multiselect") return 160;
        if (col.type === "checkbox" || col.type === "radio") return 180;
        if (col.type === "datetime") return 150;
        if (col.type === "date") return 130;
        if (col.type === "file") return 140;
        return 90;
      };

      const columns = [
        ...cols.map((col) => ({
          title: col.label,
          dataIndex: col.id,
          key: col.id,
          width: columnWidth(col),
          render: (_, record) =>
            renderCell(col, record.__row?.[col.id], (v) =>
              updateCell(record.__idx, col.id, v)
            ),
        })),
        ...(allowAddRows
          ? [
              {
                title: "",
                dataIndex: "__actions",
                key: "__actions",
                width: 48,
                render: (_, record) =>
                  canDelete(record.__idx) ? (
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={() => deleteRow(record.__idx)}
                    >
                      ✕
                    </Button>
                  ) : null,
              },
            ]
          : []),
      ];

      const data = currentRows.map((row, idx) => ({
        key: idx,
        __idx: idx,
        __row: row || {},
      }));

      const scrollX =
        cols.reduce((sum, col) => sum + columnWidth(col), 0) +
        (allowAddRows ? 48 : 0);

      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Table
            size="small"
            bordered
            pagination={false}
            dataSource={data}
            columns={columns}
            tableLayout="fixed"
            scroll={{ x: scrollX }}
          />
          {canAdd && (
            <Button size="small" onClick={addRow}>
              + Add row
            </Button>
          )}
        </Space>
      );
    }
    case FIELD_TYPES.FILE:
      return (
        <Upload>
          <Button icon={<UploadOutlined />}>Upload file</Button>
        </Upload>
      );
    default:
      return null;
  }
};

const MULTI_VALUE_TYPES = [FIELD_TYPES.MULTISELECT, FIELD_TYPES.CHECKBOX];

const initialValueFor = (type) => {
  if (MULTI_VALUE_TYPES.includes(type)) return [];
  if (type === FIELD_TYPES.TABLE) return [];
  return undefined;
};

const isSelected = (value, optValue) =>
  Array.isArray(value) ? value.includes(optValue) : value === optValue;

const NestedFields = ({ fields }) => (
  <div
    style={{
      marginTop: 12,
      marginLeft: 32,
      paddingLeft: 20,
      borderLeft: "2px solid #eaecf0",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    }}
  >
    {(fields || []).map((f) => (
      <PreviewField key={f.id} field={f} />
    ))}
  </div>
);

const PreviewField = ({ field, primaryColor }) => {
  const [value, setValue] = useState(() => initialValueFor(field.type));

  if (field.type === FIELD_TYPES.SECTION_HEADER) {
    return (
      <Flex align="center" gap={8}>
        <span
          style={{
            width: 3,
            height: 14,
            background: primaryColor || "var(--drf-primary, #7f56d9)",
            borderRadius: 2,
            display: "inline-block",
          }}
        />
        <Typography.Text strong style={{ fontSize: 12, letterSpacing: "0.06em" }}>
          {(field.label || "Group").toUpperCase()}
        </Typography.Text>
      </Flex>
    );
  }

  const directSubs = field.sub_questions || [];
  // RADIO/CHECKBOX interleave sub-questions inline with each option (so
  // they appear directly under the selected option). For dropdown-based
  // types (SELECT/MULTISELECT) we surface them in a list below the control,
  // since we can't interleave inside a popover.
  const isInlineSubsType =
    field.type === FIELD_TYPES.RADIO || field.type === FIELD_TYPES.CHECKBOX;
  const dropdownOptionSubs = isInlineSubsType
    ? []
    : (field.options || [])
        .filter(
          (o) =>
            typeof o === "object" &&
            o?.sub_questions?.length &&
            isSelected(value, o.value)
        )
        .flatMap((o) => o.sub_questions);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #eaecf0",
        borderRadius: 12,
        padding: "20px 24px",
      }}
    >
      <Typography.Text strong>
        {field.label || "Untitled field"}
        {field.required && (
          <span style={{ color: "#f04438", marginLeft: 4 }}>*</span>
        )}
      </Typography.Text>
      <div style={{ marginTop: 10 }}>{renderControl(field, value, setValue)}</div>

      {directSubs.length > 0 && <NestedFields fields={directSubs} />}
      {dropdownOptionSubs.length > 0 && (
        <NestedFields fields={dropdownOptionSubs} />
      )}
    </div>
  );
};

const SectionPreview = ({ section, total, index, primaryColor }) => (
  <Flex vertical gap={20} style={{ maxWidth: 760, margin: "0 auto" }}>
    <div>
      <Typography.Title level={4} style={{ margin: 0 }}>
        {section.title}
      </Typography.Title>
      <Typography.Text type="secondary">
        Section {index + 1} of {total}
      </Typography.Text>
    </div>
    {section.fields.map((field) => (
      <PreviewField key={field.id} field={field} primaryColor={primaryColor} />
    ))}
  </Flex>
);

const formHasContent = (form) =>
  form?.sections?.some((section) => section?.fields?.length > 0);

const PreviewModal = ({ open, onClose, role, forms, initialFormId }) => {
  const { token } = theme.useToken();
  const visibleForms = (forms || []).filter(formHasContent);
  const resolveInitialId = () =>
    visibleForms.some((f) => f.id === initialFormId)
      ? initialFormId
      : visibleForms[0]?.id;
  const [activeFormId, setActiveFormId] = useState(resolveInitialId);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveFormId(resolveInitialId());
      setActiveSectionIdx(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFormId, forms]);

  const activeForm =
    visibleForms.find((f) => f.id === activeFormId) || visibleForms[0];
  const activeSection = activeForm?.sections[activeSectionIdx];

  const primaryTint = `color-mix(in srgb, ${token.colorPrimary} 14%, white)`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width="100vw"
      style={{ top: 0, paddingBottom: 0, maxWidth: "100vw" }}
      styles={{
        content: { height: "100vh", borderRadius: 0, padding: 0 },
        body: { height: "100vh", padding: 0, overflow: "hidden" },
      }}
      destroyOnClose
    >
      <Flex vertical style={{ height: "100vh", background: "#ffffff" }}>
        <Flex
          justify="space-between"
          align="center"
          gap={16}
          style={{
            padding: "14px 24px",
            borderBottom: "1px solid #eaecf0",
            background: "#ffffff",
          }}
        >
          <Flex align="center" gap={10} style={{ minWidth: 200 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: token.colorPrimary,
                color: "#ffffff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
            >
              <CalendarOutlined />
            </div>
            <div>
              <Typography.Text strong style={{ fontSize: 16 }}>
                {role?.name}
              </Typography.Text>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Form Preview
                </Typography.Text>
              </div>
            </div>
          </Flex>

          <Segmented
            value={activeForm?.id}
            options={visibleForms.map((f) => ({ label: f.name, value: f.id }))}
            onChange={(id) => {
              setActiveFormId(id);
              setActiveSectionIdx(0);
            }}
          />

          <Flex align="center" gap={12} style={{ minWidth: 200, justifyContent: "flex-end" }}>
            <Tag
              icon={<EyeOutlined />}
              color="default"
              style={{
                borderRadius: 999,
                padding: "2px 12px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Preview Mode
            </Tag>
            <CloseOutlined
              onClick={onClose}
              style={{ fontSize: 18, color: "#667085", cursor: "pointer", padding: 6 }}
            />
          </Flex>
        </Flex>

        {activeForm && (
          <Flex
            justify="center"
            gap={8}
            wrap
            style={{
              padding: "12px 24px",
              background: "#ffffff",
              borderBottom: "1px solid #eaecf0",
            }}
          >
            {activeForm.sections.map((s, idx) => {
              const done = idx < activeSectionIdx;
              const current = idx === activeSectionIdx;
              return (
                <Tag
                  key={s.id}
                  onClick={() => setActiveSectionIdx(idx)}
                  style={{
                    cursor: "pointer",
                    padding: "4px 12px",
                    borderRadius: 999,
                    fontSize: 13,
                    background: current ? primaryTint : "transparent",
                    color: current || done ? token.colorPrimary : "#667085",
                    border: current ? "none" : "1px solid #eaecf0",
                  }}
                  icon={
                    done ? (
                      <CheckOutlined />
                    ) : current ? (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          background: token.colorPrimary,
                          display: "inline-block",
                          marginRight: 6,
                        }}
                      />
                    ) : null
                  }
                >
                  {s.title}
                </Tag>
              );
            })}
          </Flex>
        )}

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            background: "#f7f8fa",
            padding: "32px 24px",
          }}
        >
          {activeSection ? (
            <SectionPreview
              section={activeSection}
              total={activeForm.sections.length}
              index={activeSectionIdx}
              primaryColor={token.colorPrimary}
            />
          ) : (
            <Flex
              justify="center"
              align="center"
              style={{ height: "100%", color: "#98a2b3" }}
            >
              <Typography.Text type="secondary">
                No form content to preview yet. Add fields to a form and save to
                see it here.
              </Typography.Text>
            </Flex>
          )}
        </div>
      </Flex>
    </Modal>
  );
};

export default PreviewModal;
